import { toast } from '@/shared/hooks/use-toast';
import { clearFunnelIntents } from '@/shared/utils/pricingIntent';

interface TokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

class TokenManager {
  private refreshTimeout: number | null = null;
  private expirationTimeout: number | null = null;
  private isRefreshing = false;
  private lastActivityTime: number = Date.now();
  private activityCheckInterval: number | null = null;
  private boundUpdateActivity: () => void;

  constructor() {
    // Bind the updateActivity method to this instance for proper event listener management
    this.boundUpdateActivity = this.updateActivity.bind(this);
  }


  // Get current access token
  getAccessToken(): string | null {
    return localStorage.getItem('dashAccessToken') || localStorage.getItem('authToken');
  }

  // Get current refresh token
  getRefreshToken(): string | null {
    return localStorage.getItem('refreshToken') || localStorage.getItem('dashRefreshToken');
  }

  // Set tokens in localStorage
  setTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem('dashAccessToken', accessToken);
    localStorage.setItem('authToken', accessToken); // Also set authToken for backwards compatibility
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('dashRefreshToken', refreshToken); // Also set dashRefreshToken for consistency
    this.scheduleTokenRefresh();
  }

  // Clear all tokens
  clearTokens(isLogout: boolean = false, suppressModal: boolean = false): void {
    localStorage.removeItem('dashAccessToken');
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('dashRefreshToken');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('midash:ui_state');
    sessionStorage.removeItem('main-chat-session');

    // On a MANUAL logout, purge per-browser funnel intents (pending trial/
    // subscribe CTA + campaign coupon) so they can't fire for the NEXT account
    // that logs in on this machine. Skipped on session-expiry token clears
    // (isLogout=false) where the SAME user re-authenticates and a pending intent
    // should survive.
    if (isLogout) {
      clearFunnelIntents();
    }

    this.clearTimeouts();

    // Remove activity event listeners - use the bound method reference
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click', 'mousemove'];
    activityEvents.forEach(event => {
      document.removeEventListener(event, this.boundUpdateActivity);
    });

    // Only show session expired modal if this is NOT a manual logout AND not suppressed
    if (!isLogout && !suppressModal) {
      this.showSessionExpiredModal();
    }
  }

  // Check if token is expired
  isTokenExpired(): boolean {
    const token = this.getAccessToken();
    if (!token) return true;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      return payload.exp < currentTime;
    } catch (error) {
      console.error('Error parsing token:', error);
      return true;
    }
  }

  // Get token expiration time
  getTokenExpirationTime(): number | null {
    const token = this.getAccessToken();
    if (!token) return null;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000; // Convert to milliseconds
    } catch (error) {
      console.error('Error parsing token expiration:', error);
      return null;
    }
  }

  // Get time until token expires (in milliseconds)
  getTimeUntilExpiration(): number {
    const expirationTime = this.getTokenExpirationTime();
    if (!expirationTime) return 0;

    const currentTime = Date.now();
    return Math.max(0, expirationTime - currentTime);
  }

  // Refresh token
  async refreshToken(): Promise<boolean> {
    if (this.isRefreshing) {
      console.log('Token refresh already in progress');
      return false;
    }

    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      console.error('No refresh token available');
      this.handleTokenExpiration();
      return false;
    }

    this.isRefreshing = true;

    try {
      const apiUrl = (import.meta as any).env.VITE_API_URL || '';
      const response = await fetch(`${apiUrl}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (response.ok) {
        const data: TokenResponse = await response.json();
        this.setTokens(data.access_token, data.refresh_token);
        console.log('✅ Token refreshed successfully');
        this.isRefreshing = false;
        return true;
      } else {
        console.error('❌ Token refresh failed:', response.status);
        this.isRefreshing = false;
        this.handleTokenExpiration();
        return false;
      }
    } catch (error) {
      console.error('❌ Error refreshing token:', error);
      this.isRefreshing = false;
      this.handleTokenExpiration();
      return false;
    }
  }

  // Schedule token refresh
  scheduleTokenRefresh(): void {
    this.clearTimeouts();

    const timeUntilExpiration = this.getTimeUntilExpiration();
    if (timeUntilExpiration <= 0) {
      console.error('Token already expired');
      this.handleTokenExpiration();
      return;
    }

    // Refresh token 5 minutes before expiration
    const refreshTime = Math.max(timeUntilExpiration - (5 * 60 * 1000), 60000); // At least 1 minute
    const expirationTime = timeUntilExpiration;

    console.log(`🕐 Token expires in ${Math.round(expirationTime / 1000 / 60)} minutes`);
    console.log(`🔄 Will refresh token in ${Math.round(refreshTime / 1000 / 60)} minutes`);

    // Schedule refresh
    this.refreshTimeout = setTimeout(async () => {
      console.log('🔄 Attempting to refresh token...');
      const success = await this.refreshToken();
      if (!success) {
        this.handleTokenExpiration();
      }
    }, refreshTime);

    // Schedule session extension dialog (2 minutes before)
    const warningTime = Math.max(expirationTime - (2 * 60 * 1000), 30000);
    this.expirationTimeout = setTimeout(() => {
      this.showSessionExtensionDialog();
    }, warningTime);
  }

  // Show session extension dialog
  showSessionExtensionDialog(): void {
    // Create a custom toast with action buttons
    toast({
      title: "Session Expiring Soon",
      description: "Your session will expire in 2 minutes. Would you like to stay logged in?",
      variant: "default",
      duration: 0, // Don't auto-dismiss
      action: {
        label: "Stay Logged In",
        onClick: () => this.extendSession(),
      },
      cancel: {
        label: "Logout Now",
        onClick: () => this.handleTokenExpiration(),
      },
    });
  }

  // Extend session by refreshing token
  async extendSession(): Promise<void> {
    try {
      const success = await this.refreshToken();
      if (!success) {
        this.handleTokenExpiration();
      }
      // No toast notification - session extension happens silently in background
    } catch (error) {
      console.error('Failed to extend session:', error);
      this.handleTokenExpiration();
    }
  }

  // Manual logout (don't show session expired modal)
  logout(): void {
    console.log('🚪 User manually logging out');
    this.clearTokens(true); // Pass true to indicate this is a manual logout
    // Dispatch userLogout event instead of tokenExpired for manual logout
    window.dispatchEvent(new CustomEvent('userLogout'));
  }

  // Check if user is currently on a dashboard route (protected area)
  private isDashboardRoute(): boolean {
    const path = window.location.pathname;
    // Dashboard routes that require the session expired modal
    const dashboardPaths = ['/platform', '/settings', '/agents', '/strategies'];
    return dashboardPaths.some(dashPath => path.startsWith(dashPath));
  }

  // Check if this was a recently active session (not stale tokens from long ago)
  private isRecentSession(): boolean {
    const token = this.getAccessToken();
    if (!token) return false;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      const timeSinceExpiry = currentTime - (payload.exp || 0);

      // If token expired more than 24 hours ago, it's a stale session
      // Don't show modal for old sessions
      return timeSinceExpiry <= 86400;
    } catch (e) {
      // Invalid token format
      return false;
    }
  }

  // Check if user is on a protected route (requires authentication)
  private isProtectedRoute(): boolean {
    // Check if user is logged in by checking if we have valid tokens
    const hasTokens = !!this.getAccessToken() && !!this.getRefreshToken();
    return hasTokens && this.isRecentSession();
  }

  // Handle token expiration
  handleTokenExpiration(): void {
    console.log('🚨 Token expired - logging out user');

    // Determine if we should show modal based on context:
    // 1. Must be a recent session (not stale tokens from weeks ago)
    // 2. Must be on a dashboard route (not public pages like homepage)
    const isRecent = this.isRecentSession();
    const isOnDashboard = this.isDashboardRoute();
    const shouldShowModal = isRecent && isOnDashboard;

    console.log(`📍 Session context: recent=${isRecent}, dashboard=${isOnDashboard}, showModal=${shouldShowModal}`);

    // Clear tokens (show modal only if on dashboard with recent session)
    this.clearTokens(false, !shouldShowModal);

    // For public pages with recent sessions, show a subtle toast instead of modal
    if (isRecent && !isOnDashboard) {
      this.showSessionExpiredToast();
    }

    // Dispatch logout event
    window.dispatchEvent(new CustomEvent('tokenExpired'));
  }

  // Show a subtle toast notification for session expiry on public pages
  private showSessionExpiredToast(): void {
    toast({
      title: "Session Expired",
      description: "You've been logged out. Log in again to access your dashboard.",
      variant: "default",
      duration: 5000, // 5 seconds
    });
  }

  // Show session expired modal
  private showSessionExpiredModal(): void {
    // Check if modal already exists
    const existingModal = document.getElementById('session-expired-modal');
    if (existingModal) {
      return;
    }

    // Create modal element with better structure and design matching BrokerConnectionModal
    const modal = document.createElement('div');
    modal.id = 'session-expired-modal';
    modal.className = 'fixed inset-0 z-[100] flex items-center justify-center p-4';
    
    // Use separate backdrop and content for better control
    modal.innerHTML = `
      <!-- Backdrop -->
      <div class="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>
      
      <!-- Modal Content -->
      <div class="relative bg-[#0F1114] border border-gray-800 rounded-xl shadow-2xl max-w-md w-full mx-auto transform transition-all">
        <!-- Header -->
        <div class="p-6 pb-0">
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-xl font-semibold text-gray-100">Session Expired</h2>
            <div class="w-10 h-10 bg-red-500/10 rounded-full flex items-center justify-center">
              <svg class="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
          </div>
        </div>

        <!-- Body -->
        <div class="p-6 pt-2">
          <div class="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-lg mb-6">
            <svg class="h-5 w-5 text-red-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 3 1.732 3z"></path>
            </svg>
            <div class="flex-1">
              <div class="text-sm font-medium text-red-400 mb-1">Your session has expired</div>
              <div class="text-xs text-red-300/80">Please log in again to continue using the dashboard</div>
            </div>
          </div>

          <!-- Action Button -->
          <button
            id="relogin-btn"
            class="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-all duration-200 hover:shadow-lg hover:shadow-blue-600/20 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-[#0F1114]"
            style="will-change: transform;"
          >
            <div class="flex items-center justify-center gap-2">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"></path>
              </svg>
              <span>Go to Login</span>
            </div>
          </button>
        </div>
      </div>
    `;

    // Add to DOM
    document.body.appendChild(modal);
    
    // Force reflow to ensure animation plays
    modal.offsetHeight;
    
    // Add animation class after a tick
    requestAnimationFrame(() => {
      const content = modal.querySelector('.relative');
      if (content) {
        content.classList.add('animate-in', 'fade-in', 'zoom-in-95');
      }
    });

    // Handle relogin button click
    const reloginBtn = modal.querySelector('#relogin-btn');
    if (reloginBtn) {
      reloginBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        // Add fade out animation before removing
        modal.classList.add('animate-out', 'fade-out');
        setTimeout(() => {
          document.body.removeChild(modal);
          window.location.href = '/login?session=expired';
        }, 150);
      });
    }

    // Prevent any interaction with backdrop
    modal.addEventListener('click', (e) => {
      e.stopPropagation();
      // Don't allow dismissing by clicking outside
      if (e.target === modal || e.target === modal.firstElementChild) {
        return false;
      }
    });
  }

  // Clear all timeouts
  private clearTimeouts(): void {
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
      this.refreshTimeout = null;
    }
    if (this.expirationTimeout) {
      clearTimeout(this.expirationTimeout);
      this.expirationTimeout = null;
    }
    if (this.activityCheckInterval) {
      clearInterval(this.activityCheckInterval);
      this.activityCheckInterval = null;
    }
  }

  // Initialize token management
  initialize(): void {
    const token = this.getAccessToken();
    if (token && !this.isTokenExpired()) {
      this.scheduleTokenRefresh();
      this.startActivityMonitoring();
    } else if (token && this.isTokenExpired()) {
      console.log('Token is expired, attempting refresh...');
      this.refreshToken();
    }
  }

  // Start monitoring user activity
  private startActivityMonitoring(): void {
    // Track user activity events
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click', 'mousemove'];

    activityEvents.forEach(event => {
      document.addEventListener(event, this.boundUpdateActivity, { passive: true });
    });

    // Check activity every 30 seconds
    this.activityCheckInterval = setInterval(() => {
      this.checkActivity();
    }, 30000);
  }

  // Update last activity time
  private updateActivity(): void {
    this.lastActivityTime = Date.now();
  }

  // Check if user is active and auto-extend if needed
  private async checkActivity(): Promise<void> {
    const timeSinceLastActivity = Date.now() - this.lastActivityTime;
    const timeUntilExpiration = this.getTimeUntilExpiration();

    // If user has been active in the last 5 minutes and token expires in less than 10 minutes
    if (timeSinceLastActivity < 5 * 60 * 1000 && timeUntilExpiration < 10 * 60 * 1000) {
      console.log('User is active, auto-extending session...');
      await this.extendSession();
    }
  }
}

// Export singleton instance
export const tokenManager = new TokenManager();
